import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Empty, Table, Field, Input, Select, Pill } from "../hr/ui";
import { Sev, Spinner, Tabs } from "./mui";
import { Plus, Play, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const emptyTask = { title: "", description: "", assigned_to: "", location_id: "", priority: "medium", due_at: "" };
const emptyList = { name: "", description: "", location_id: "", recurrence_type: "daily", every: 1, items: "" };

export default function TasksChecklists() {
  const { locationId, locations } = useOutletContext();
  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState(null);
  const [checklists, setChecklists] = useState(null);
  const [occurrences, setOccurrences] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const [listForm, setListForm] = useState(null);
  const [openOcc, setOpenOcc] = useState(null);

  const load = useCallback(() => {
    const lp = { location_id: locationId || undefined };
    api.get("/management/tasks", { params: { ...lp, status: status || undefined } }).then(setTasks).catch((e) => toast.error(formatApiError(e)));
    api.get("/management/checklists", { params: lp }).then(setChecklists).catch(() => {});
    api.get("/management/checklists/occurrences", { params: lp }).then(setOccurrences).catch(() => {});
  }, [locationId, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/hr/employees", { params: { limit: 500 } }).then((d) => setEmployees(d?.rows || d || [])).catch(() => {}); }, []);

  const saveTask = async () => {
    if (!form.title.trim()) return toast.error("A task title is required");
    try {
      await api.post("/management/tasks", {
        ...form, assigned_to: form.assigned_to || null, location_id: form.location_id || null,
        due_at: form.due_at ? `${form.due_at}:00` : null,
        recurrence: form.recurrence_type ? { type: form.recurrence_type, every: Number(form.every || 1) } : null,
      });
      toast.success("Task assigned"); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const advance = async (t, next) => {
    try { await api.put(`/management/tasks/${t.id}`, { status: next }); toast.success(`Task ${next.replace("_", " ")}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveList = async () => {
    if (!listForm.name.trim()) return toast.error("A checklist name is required");
    try {
      await api.post("/management/checklists", {
        name: listForm.name, description: listForm.description,
        location_id: listForm.location_id || null,
        recurrence: { type: listForm.recurrence_type, every: Number(listForm.every || 1) },
        items: listForm.items.split("\n").map((s) => s.trim()).filter(Boolean).map((description) => ({ description, required: true })),
      });
      toast.success("Checklist created"); setListForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeItem = async (occ, item) => {
    try {
      const updated = await api.post(`/management/checklists/occurrences/${occ.id}/items/${item.item_id}`, { status: "completed" });
      setOpenOcc(updated); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sweep = async () => {
    try { const r = await api.post("/management/run-sweep"); toast.success(`Sweep done · ${r.missed} missed, ${r.generated} generated`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!tasks) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="mgmt-tasks-page">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs testid="tasks-tabs" value={tab} onChange={setTab}
          tabs={[["tasks", "Tasks"], ["checklists", "Checklists"], ["occurrences", "Checklist Runs"]]} />
        <div className="flex-1" />
        <Btn variant="outline" onClick={sweep} data-testid="tasks-run-sweep"><RefreshCw size={15} /> Run sweep</Btn>
        {tab === "checklists"
          ? <Btn variant="primary" onClick={() => setListForm({ ...emptyList })} data-testid="new-checklist-btn"><Plus size={15} /> New Checklist</Btn>
          : <Btn variant="primary" onClick={() => setForm({ ...emptyTask, recurrence_type: "" })} data-testid="new-task-btn"><Plus size={15} /> New Task</Btn>}
      </div>

      {tab === "tasks" && (
        <Card testid="tasks-card">
          <SectionHead title="Management Tasks" sub={`${tasks.length} task(s)`}
            right={
              <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44" data-testid="tasks-status-filter">
                <option value="">All statuses</option>
                {["assigned", "in_progress", "completed", "missed", "cancelled"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </Select>
            } />
          <Table testid="tasks-table" columns={[
            { key: "title", label: "Task", render: (r) => (
              <div><div className="font-semibold">{r.title}</div><div className="text-[11px]" style={{ color: HC.soft }}>{r.description}</div></div>
            ) },
            { key: "assignee_name", label: "Assignee" },
            { key: "location_name", label: "Location" },
            { key: "priority", label: "Priority", render: (r) => <Sev value={r.priority} /> },
            { key: "due_at", label: "Due", render: (r) => fmtDateTime(r.due_at) },
            { key: "status", label: "Status", render: (r) => <Pill status={r.status} /> },
            { key: "act", label: "", align: "right", render: (r) => (
              <div className="flex gap-2 justify-end">
                {r.status === "assigned" && <Btn variant="outline" onClick={() => advance(r, "in_progress")} data-testid={`task-start-${r.id}`}><Play size={13} /> Start</Btn>}
                {["assigned", "in_progress", "missed"].includes(r.status) && (
                  <Btn variant="primary" onClick={() => advance(r, "completed")} data-testid={`task-complete-${r.id}`}><Check size={13} /> Complete</Btn>
                )}
              </div>
            ) },
          ]} rows={tasks} empty="No tasks yet" />
        </Card>
      )}

      {tab === "checklists" && (
        <Card testid="checklists-card">
          <SectionHead title="Recurring Checklists" sub="Opening, closing and hygiene routines" />
          <Table testid="checklists-table" columns={[
            { key: "name", label: "Checklist" },
            { key: "location_name", label: "Location" },
            { key: "recurrence", label: "Recurrence", render: (r) => `Every ${r.recurrence?.every || 1} ${r.recurrence?.type || "day"}` },
            { key: "item_count", label: "Items", align: "right" },
            { key: "next_due_at", label: "Next due", render: (r) => fmtDateTime(r.next_due_at) },
            { key: "latest", label: "Latest run", render: (r) => r.latest_occurrence ? <Pill status={r.latest_occurrence.status} /> : "—" },
          ]} rows={checklists || []} empty="No checklists configured" />
        </Card>
      )}

      {tab === "occurrences" && (
        <Card testid="occurrences-card">
          <SectionHead title="Checklist Runs" sub="Scheduled occurrences and completion" />
          <Table testid="occurrences-table" onRowClick={setOpenOcc} columns={[
            { key: "checklist_name", label: "Checklist" },
            { key: "location_name", label: "Location" },
            { key: "scheduled_for", label: "Scheduled", render: (r) => fmtDateTime(r.scheduled_for) },
            { key: "completion_pct", label: "Complete", align: "right", render: (r) => `${r.completion_pct}%` },
            { key: "outstanding_items", label: "Outstanding", align: "right" },
            { key: "status", label: "Status", render: (r) => <Pill status={r.status} /> },
          ]} rows={occurrences || []} empty="No checklist runs yet" />
        </Card>
      )}

      {form && (
        <Modal title="Assign a task" onClose={() => setForm(null)} onSave={saveTask} testid="task-modal">
          <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></Field>
          <Field label="Description"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Assignee">
              <Select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="task-assignee-select">
                <option value="">Unassigned</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </Select>
            </Field>
            <Field label="Location">
              <Select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} data-testid="task-location-select">
                <option value="">All locations</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority-select">
                {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Due"><Input type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="task-due-input" /></Field>
            <Field label="Recurrence">
              <Select value={form.recurrence_type} onChange={(e) => setForm({ ...form, recurrence_type: e.target.value })} data-testid="task-recurrence-select">
                <option value="">One-off</option>
                {["daily", "weekly", "monthly"].map((r) => <option key={r} value={r}>{r}</option>)}
              </Select>
            </Field>
            {form.recurrence_type && (
              <Field label="Every"><Input type="number" min="1" value={form.every || 1} onChange={(e) => setForm({ ...form, every: e.target.value })} /></Field>
            )}
          </div>
        </Modal>
      )}

      {listForm && (
        <Modal title="New checklist" onClose={() => setListForm(null)} onSave={saveList} testid="checklist-modal">
          <Field label="Name"><Input value={listForm.name} onChange={(e) => setListForm({ ...listForm, name: e.target.value })} data-testid="checklist-name-input" /></Field>
          <Field label="Description"><Input value={listForm.description} onChange={(e) => setListForm({ ...listForm, description: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Location">
              <Select value={listForm.location_id} onChange={(e) => setListForm({ ...listForm, location_id: e.target.value })} data-testid="checklist-location-select">
                <option value="">All locations</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </Field>
            <Field label="Recurrence">
              <Select value={listForm.recurrence_type} onChange={(e) => setListForm({ ...listForm, recurrence_type: e.target.value })}>
                {["daily", "weekly", "monthly"].map((r) => <option key={r} value={r}>{r}</option>)}
              </Select>
            </Field>
            <Field label="Every"><Input type="number" min="1" value={listForm.every} onChange={(e) => setListForm({ ...listForm, every: e.target.value })} /></Field>
          </div>
          <Field label="Items (one per line)">
            <textarea rows={6} value={listForm.items} onChange={(e) => setListForm({ ...listForm, items: e.target.value })}
              data-testid="checklist-items-input" className="w-full p-3 rounded-xl text-sm bg-white outline-none"
              style={{ border: `1px solid ${HC.border}`, color: HC.text }} />
          </Field>
        </Modal>
      )}

      {openOcc && (
        <Modal title={openOcc.checklist_name} onClose={() => setOpenOcc(null)} testid="occurrence-modal">
          <div className="space-y-2">
            {(openOcc.items || []).map((i) => (
              <div key={i.item_id} className="flex items-center justify-between gap-3 rounded-xl border p-3" style={{ borderColor: HC.border }}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold" style={{ color: HC.text }}>{i.description}</div>
                  <div className="text-[11px]" style={{ color: HC.soft }}>
                    {i.status === "completed" ? `Done by ${i.completed_by} · ${fmtDateTime(i.completed_at)}` : "Pending"}
                  </div>
                </div>
                {i.status === "completed"
                  ? <Pill status="completed" />
                  : <Btn variant="primary" onClick={() => completeItem(openOcc, i)} data-testid={`occ-item-complete-${i.item_id}`}><Check size={13} /> Done</Btn>}
              </div>
            ))}
            {!(openOcc.items || []).length && <Empty text="No items on this checklist" />}
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, children, onClose, onSave, saveLabel = "Save", testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: HC.border }}>
          <div className="font-brand font-bold text-lg" style={{ color: HC.text }}>{title}</div>
          <Btn variant="outline" onClick={onClose} data-testid={`${testid}-close`}>Close</Btn>
        </div>
        <div className="p-4 overflow-auto space-y-3">{children}</div>
        {onSave && (
          <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: HC.border }}>
            <Btn variant="outline" onClick={onClose}>Cancel</Btn>
            <Btn variant="primary" onClick={onSave} data-testid={`${testid}-save`}>{saveLabel}</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
